import React from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import A_ChatAvatar from '@/components/A_Atoms/A_ChatAvatar/A_ChatAvatar';
import M_CapsuleCard from '@/components/M_Molecules/M_CapsuleCard/M_CapsuleCard';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { ChatCapsulePreview, ChatMessage } from '@/services/chatApi';
import { PATHS } from '@/constants/paths';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  displayName,
  formatMessageTime,
  systemMessageText,
} from '@/utils/chat';
import styles from './M_ChatMessage.module.css';

interface M_ChatMessageProps {
  message: ChatMessage;
  isOwn: boolean;
  currentUserId?: string;
  /** В группах у входящих сообщений показываем автора. */
  showAuthor?: boolean;
  onRetry?: (clientId: string) => void;
}

/** Ссылки внутри текста: капсулы открываются внутри приложения, прочие — в новой вкладке. */
const LINK_PATTERN = /((?:https?:\/\/[^\s]+)|(?:\/capsule\/\d+))/g;
const CAPSULE_LINK_PATTERN = /\/capsule\/(\d+)/;

function renderTextWithLinks(text: string): React.ReactNode[] {
  return text.split(LINK_PATTERN).map((part, index) => {
    if (index % 2 === 0) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }

    const capsuleMatch = part.match(CAPSULE_LINK_PATTERN);
    if (capsuleMatch) {
      return (
        <Link
          key={index}
          to={PATHS.CAPSULE_BY_ID(capsuleMatch[1])}
          className={styles.link}
        >
          {part}
        </Link>
      );
    }

    return (
      <a
        key={index}
        href={part}
        target='_blank'
        rel='noreferrer'
        className={styles.link}
      >
        {part}
      </a>
    );
  });
}

function CapsuleAttachment({
  capsule,
  currentUserId,
  unavailableLabel,
}: {
  capsule: ChatCapsulePreview;
  currentUserId?: string;
  unavailableLabel: string;
}) {
  if (!capsule.isAccessible || !capsule.author) {
    return (
      <div className={cn(styles.capsule_locked, 'text-caption')}>
        <Q_Icon.CapsIcon width={20} height={20} fill='none' />
        <span>{unavailableLabel}</span>
      </div>
    );
  }

  const coauthorAvatars = (capsule.coauthors || [])
    .map(coauthor => coauthor.avatar)
    .filter(Boolean) as string[];

  return (
    <div className={styles.capsule_card}>
      <M_CapsuleCard
        id={capsule.id}
        title={capsule.title || ''}
        image={capsule.imagePreview || capsule.image || undefined}
        avatars={[capsule.author.avatar || '', ...coauthorAvatars].filter(
          Boolean
        )}
        coauthors={capsule.coauthors?.length || 0}
        commentsCount={capsule.commentsCount}
        isMyCapsule={capsule.author.id === currentUserId}
        ownerName={displayName(capsule.author)}
        ownerId={capsule.author.id}
        isAdminCapsule={Boolean(capsule.author.is_administrator)}
      />
    </div>
  );
}

export default function M_ChatMessage({
  message,
  isOwn,
  currentUserId,
  showAuthor = false,
  onRetry,
}: M_ChatMessageProps) {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);

  if (message.kind === 'system') {
    const text = systemMessageText(message, t);
    if (!text) return null;
    return <div className={cn(styles.system, 'text-caption')}>{text}</div>;
  }

  if (message.kind === 'mediator') {
    return (
      <div className={styles.mediator}>
        <div className={styles.mediator_header}>
          <Q_Icon.ChatIcon width={16} height={16} fill='none' />
          <span className={cn(styles.mediator_title, 'text-caption')}>
            {t('chat.mediator.name')}
          </span>
          <span className={cn(styles.time, 'text-caption')}>
            {formatMessageTime(message.createdAt, language)}
          </span>
        </div>
        <p className={cn(styles.text, 'text-p-8')}>
          {renderTextWithLinks(message.text)}
        </p>
        {(message.mentions?.length ?? 0) > 0 && (
          <div className={styles.mentions}>
            {message.mentions?.map(user => (
              <Link
                key={user.id}
                to={PATHS.USER_PROFILE_BY_HANDLE(user.username)}
                className={cn(styles.mention, 'text-caption')}
              >
                @{user.username}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  const authorName = displayName(message.sender);

  return (
    <div className={cn(styles.row, { [styles.row_own]: isOwn })}>
      {!isOwn && (
        <div className={styles.avatar_slot}>
          {showAuthor && (
            <A_ChatAvatar
              title={authorName}
              src={message.sender?.avatar}
              size={32}
            />
          )}
        </div>
      )}
      <div
        className={cn(styles.bubble, {
          [styles.bubble_own]: isOwn,
          [styles.bubble_failed]: message.isFailed,
        })}
      >
        {showAuthor && !isOwn && (
          <span className={cn(styles.author, 'text-caption')}>
            {authorName}
          </span>
        )}

        {message.capsules.length > 0 && (
          <div className={styles.capsules}>
            {message.capsules.map(capsule => (
              <CapsuleAttachment
                key={capsule.id}
                capsule={capsule}
                currentUserId={currentUserId}
                unavailableLabel={t('chat.message.capsuleUnavailable')}
              />
            ))}
          </div>
        )}

        {message.text && (
          <p className={cn(styles.text, 'text-p-8')}>
            {renderTextWithLinks(message.text)}
          </p>
        )}

        <div className={styles.meta}>
          {message.isFailed ? (
            <button
              type='button'
              className={cn(styles.retry, 'text-caption')}
              onClick={() => message.clientId && onRetry?.(message.clientId)}
            >
              {t('chat.message.retry')}
            </button>
          ) : (
            <span className={cn(styles.time, 'text-caption')}>
              {message.isPending
                ? t('chat.message.sending')
                : formatMessageTime(message.createdAt, language)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
